<template>
  <div class="va-date-picker">
    <div :data-toggle="!disabled">
      <va-input
        v-model="textValue"
        :placeholder="placeholder"
        :label="label"
        :disabled="disabled"
        :error="error"
        :success="success"
        :messages="messages"
        :error-messages="errorMessages"
        @focus="onInputFocus"
        @blur="onInputBlur"
        @keydown.enter="commitInput"
      >
        <template slot="append">
          <va-icon color="gray" name="fa fa-calendar"/>
        </template>
      </va-input>
    </div>
    <vue-flatpickr-component
      class="va-date-picker__flatpickr"
      v-model="pickerValue"
      :config="fullConfig"
      :disabled="disabled"
      @on-change="onPickerChange"
      @on-open="onOpen"
      data-input
    />
  </div>
</template>

<script>
import VueFlatpickrComponent from "vue-flatpickr-component";
import moment from "moment";

export default {
  name: "editable-date-picker",
  components: {
    VueFlatpickrComponent,
  },
  props: {
    value: {
      required: true,
    },
    weekDays: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      defualt: "",
    },
    label: {
      type: String,
      defualt: "",
    },
    disabled: {
      type: Boolean,
    },
    error: {
      type: Boolean,
    },
    success: {
      type: Boolean,
    },
    messages: {
      type: Array,
      default: () => [],
    },
    errorMessages: {
      type: Array,
      default: () => [],
    },
    config: {
      type: Object,
      default: () => {
      },
    },
    inputFormats: {
      type: Array,
      default: () => ["DD-MM-YYYY", "DD/MM/YYYY"],
    },
  },
  data() {
    return {
      isOpen: false,
      textValue: "",
      pickerValue: null,
      isTyping: false,
    };
  },
  computed: {
    fullConfig() {
      return Object.assign({}, this.defaultConfig, this.config);
    },
    defaultConfig() {
      return {
        wrap: true,
        allowInput: true,
        nextArrow: '<span aria-hidden="true" class="ion ion-ios-arrow-forward"/>',
        prevArrow: '<span aria-hidden="true" class="ion ion-ios-arrow-back"/>',
        disableMobile: true, // doesn't work without this one at all
      };
    },
  },
  methods: {
    formatValue(value) {
      if (value === null || value === undefined || value === "") {
        return "";
      }
      if (value instanceof Date) {
        return moment(value).format(this.inputFormats[0]);
      }
      return String(value);
    },
    onInputFocus() {
      this.isTyping = true;
    },
    onInputBlur() {
      this.isTyping = false;
      this.commitInput();
    },
    commitInput() {
      const raw = (this.textValue || "").toString().trim();
      if (!raw) {
        this.$emit("input", "");
        this.pickerValue = null;
        return;
      }
      const parsed = moment(raw, this.inputFormats, true);
      if (!parsed.isValid()) {
        return;
      }
      const formatted = parsed.format(this.inputFormats[0]);
      this.$emit("input", formatted);
      this.pickerValue = parsed.toDate();
      this.textValue = formatted;
    },
    onPickerChange(selectedDates, dateStr) {
      this.textValue = dateStr;
      this.$emit("input", dateStr);
    },
    onOpen(selectedDates, dateStr, pcrObject) {
      const calendar = pcrObject.calendarContainer;
      if (this.weekDays) {
        calendar.classList.add("flatpickr-calendar--show-days");
      }
    },
  },
  watch: {
    textValue(newValue) {
      this.$emit("input-raw", newValue);
    },
    value: {
      immediate: true,
      handler(newValue) {
        if (!this.isTyping) {
          this.textValue = this.formatValue(newValue);
        }
        this.pickerValue = newValue;
      },
    },
  },
};
</script>

<style lang="scss">
@import '~vuestic-ui/src/components/vuestic-sass/resources/resources';
@import '~flatpickr/dist/flatpickr.css';

$datepickerActiveBackground: $vue-darkest-blue;
$datepickerActiveColor: $vue-green;
$datepickerBackground: $light-gray3;
$datepickerText: $vue-darkest-blue;

$datepickerToday: lighten($datepickerBackground, 5);
$datepickerOtherMonth: darken($datepickerText, 40);
$datepickerWeekday: $brand-secondary;
$datepickerDayHover: $vue-darkest-blue;
$datepickerDayRange: $vue-green;
$datepickerSeparatorColor: $separator-color;

$borderPadding: 0.5rem;
$dayPadding: 0.375rem;
$daySize: 2rem;
$dayMargin: 0.6rem;

.va-date-picker {
  position: relative;

  &__container {
    display: flex;
    position: relative;
    flex-wrap: wrap;
  }

  &__flatpickr {
    position: absolute;
    visibility: hidden;
    top: 0;
    left: 0;
    height: 100%;
  }

  &__icon {
    position: absolute;
    right: 0;
    top: 0;
  }

  .flatpickr-calendar {
    background-color: $datepickerBackground;
    box-shadow: $datepicker-box-shadow;
  }

  .flatpickr-months {
    border-bottom: solid 0.0625rem $datepickerSeparatorColor;

    .flatpickr-month {
      .flatpickr-current-month {
        color: $datepickerText;
        background-color: $datepickerBackground;
      }
    }

    .flatpickr-prev-month, .flatpickr-next-month {
      color: $datepickerActiveBackground;
    }
  }
}
</style>
